import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatUnit, formatAmount, formatUnitPlural } from "@/i18n/units";
import { plural } from "../i18n/plural";
import { useTutorial } from "@/contexts/TutorialContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ShoppingCart, Sparkles, Loader2, Check, AlertTriangle, RefreshCw,
  Plus, X, ChevronDown, Search, Camera, Crown, ShoppingBag, Trash2
} from "lucide-react";
import TutorialTooltip from "@/components/TutorialTooltip";

// Categories matching products.json database
const CATEGORY_ORDER = [
  "eggs", "dairy_eggs", "meat", "deli_meat", "seafood", "vegetables", "mushrooms",
  "fruits_berries", "grains_bakery", "nuts_seeds", "spices_oils",
  "sweets_snacks", "frozen_foods", "beverages", "beverages_alcohol", "other"
];
const CATEGORY_ICONS = {
  eggs: "🥚", dairy_eggs: "🥛", meat: "🥩", deli_meat: "🌭", seafood: "🐟",
  vegetables: "🥕", mushrooms: "🍄", fruits_berries: "🍎", grains_bakery: "🍞",
  nuts_seeds: "🥜", spices_oils: "🧂", sweets_snacks: "🍫", frozen_foods: "❄️",
  beverages: "🥤", beverages_alcohol: "🍷", other: "📦",
};

export default function GroceryPage() {
  const { api } = useAuth();
  const { t, lang, onLangChange } = useLanguage();
  const { tutorialActive, tutorialStep, updateAppState, goToStep } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingDays, setGeneratingDays] = useState(null);
  const [isStale, setIsStale] = useState(false);
  // Manual add state (staging popup)
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [addingItem, setAddingItem] = useState(false);
  const [stagedItems, setStagedItems] = useState([]); // [{ name, category, icon, off_catalog, amount, unit }]
  const [purchasing, setPurchasing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [offCatalogPurged, setOffCatalogPurged] = useState(0);

  // Update tutorial state when on grocery page
  useEffect(() => {
    updateAppState({ currentPage: location.pathname });
  }, [location.pathname, updateAppState]);

  const fetchList = useCallback(async () => {
    try {
      const res = await api().get("/grocery-list");
      setList(res.data);
      // Update tutorial app state
      const hasItems = res.data?.items?.length > 0;
      const hasChecked = res.data?.items?.some(i => i.checked);
      updateAppState({ hasGroceryItems: hasItems, hasCheckedItems: hasChecked });
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, updateAppState]);

  useEffect(() => {
    fetchList();
    const stale = localStorage.getItem("mealpill_grocery_stale");
    if (stale === "true") setIsStale(true);
    // Listen for planner changes while on this page
    const handleStorage = (e) => {
      if (e.key === "mealpill_grocery_stale" && e.newValue === "true") setIsStale(true);
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fetchList]);

  // Re-fetch grocery list when UI language changes so item names re-localize
  useEffect(() => {
    return onLangChange(() => { fetchList(); });
  }, [onLangChange, fetchList]);

  const handleGenerate = async (days = 1) => {
    setGenerating(true);
    setGeneratingDays(days);
    try {
      const res = await api().post("/grocery-list/generate", { days });
      setList(res.data);
      setIsStale(false);
      localStorage.removeItem("mealpill_grocery_stale");
      // Update app state
      const hasItems = res.data?.items?.length > 0;
      updateAppState({ hasGroceryItems: hasItems });
    } catch (err) { console.error(err); }
    setGenerating(false);
    setGeneratingDays(null);
  };

  const handlePurchase = async () => {
    if (!list?.id || checkedCount === 0) return;
    const offCount = (list?.items || []).filter(i => i.checked && i.off_catalog).length;
    setPurchasing(true);
    try {
      await api().post(`/grocery-list/${list.id}/purchase`);
      await fetchList();
      // Update app state
      updateAppState({ hasPendingFridgeItems: true });
      setOffCatalogPurged(offCount);

      // Step 5 complete - advance to step 6 which will show fridge nav tooltip
      // Do NOT auto-navigate - let user click fridge themselves
      if (tutorialActive && tutorialStep === 5) {
        goToStep(6);
        // Step 6 tooltip will now point to nav-fridge
      }
    } catch (err) { console.error(err); }
    setPurchasing(false);
  };

  const toggleItem = async (itemName, checked) => {
    if (!list?.id) return;
    try {
      const res = await api().put(`/grocery-list/${list.id}/toggle`, { item_name: itemName, checked });
      setList((prev) => ({ ...prev, items: res.data.items }));
      // Update checked items state for tutorial
      const totalItems = res.data.items?.length || 0;
      const checkedItems = res.data.items?.filter(i => i.checked).length || 0;
      const allChecked = totalItems > 0 && checkedItems === totalItems;
      updateAppState({ hasCheckedItems: checkedItems > 0, allItemsChecked: allChecked });
    } catch (err) { console.error(err); }
  };

  // Clear all items from the list
  const handleClearAll = async () => {
    if (!list?.id) return;
    setClearing(true);
    try {
      await api().delete(`/grocery-list/${list.id}`);
      // Fully reset list state to prevent ghost cart
      setList({ id: null, items: [] });
      updateAppState({ hasGroceryItems: false, hasCheckedItems: false });
      setShowClearConfirm(false);
    } catch (err) { console.error(err); }
    setClearing(false);
  };

  // Manual add flow
  const openAddModal = () => {
    setShowAddModal(true);
    setAddSearch("");
    setSuggestions([]);
    setStagedItems([]);
  };

  // Add a catalog result to staging
  const addCatalogChip = (item) => {
    const name = typeof item === "string" ? item : item.name;
    if (!name) return;
    // skip if already staged (case-insensitive)
    if (stagedItems.some(s => s.name.toLowerCase() === name.toLowerCase())) {
      setAddSearch("");
      setSuggestions([]);
      return;
    }
    setStagedItems(prev => [...prev, {
      name,
      category: (typeof item === "object" && item.category) || "other",
      icon: (typeof item === "object" && item.icon) || "",
      off_catalog: false,
      amount: "",
      unit: "шт",
    }]);
    setAddSearch("");
    setSuggestions([]);
  };

  // Add an off-catalog (custom typed) chip
  const addCustomChip = () => {
    const name = addSearch.trim();
    if (!name) return;
    if (stagedItems.some(s => s.name.toLowerCase() === name.toLowerCase())) {
      setAddSearch("");
      setSuggestions([]);
      return;
    }
    setStagedItems(prev => [...prev, {
      name,
      category: "other",
      icon: "",
      off_catalog: true,
      amount: "",
      unit: "шт",
    }]);
    setAddSearch("");
    setSuggestions([]);
  };

  const removeChip = (idx) => {
    setStagedItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateChip = (idx, field, value) => {
    setStagedItems(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const handleAddToCart = async () => {
    if (stagedItems.length === 0) return;
    setAddingItem(true);
    try {
      let listId = list?.id;
      if (!listId) {
        const created = await api().post("/grocery-list/create-empty");
        listId = created.data.id;
      }
      const payload = {
        items: stagedItems.map(s => ({
          name: s.name,
          amount: s.amount,
          unit: s.unit,
          category: s.category,
        })),
      };
      const res = await api().post(`/grocery-list/${listId}/add-items-batch`, payload);
      setList(prev => prev ? { ...prev, items: res.data.items } : { id: listId, items: res.data.items });
      setShowAddModal(false);
      setStagedItems([]);
      setAddSearch("");
      setSuggestions([]);
    } catch (e) {
      console.error("Add to cart failed", e);
    } finally {
      setAddingItem(false);
    }
  };

  // Dynamic product search as user types (no category filter)
  useEffect(() => {
    if (!showAddModal) return;

    const searchProducts = async () => {
      const q = addSearch.trim();
      try {
        const url = q.length < 2
          ? `/products/search?query=&limit=20`
          : `/products/search?query=${encodeURIComponent(q)}&limit=15`;
        const res = await api().get(url);
        const results = res.data?.results || [];
        setSuggestions(results.slice(0, 15).map(r => ({
          name: r.name,
          category: r.category,
          icon: r.icon,
        })));
      } catch (err) {
        console.error(err);
      }
    };

    const debounce = setTimeout(searchProducts, 200);
    return () => clearTimeout(debounce);
  }, [addSearch, showAddModal, api]);

  const items = list?.items || [];
  const checkedCount = items.filter((i) => i.checked).length;

  
  // Separate generated vs manual items
  const generatedItems = items.filter(i => !i.manual);
  const manualItems = items.filter(i => i.manual);

  const buildGrouped = (itemList) => {
    const grouped = {};
    for (const cat of CATEGORY_ORDER) {
      const catItems = itemList.filter((i) => (i.category || "other") === cat);
      if (catItems.length > 0) grouped[cat] = catItems;
    }
    for (const item of itemList) {
      const cat = item.category || "other";
      if (!CATEGORY_ORDER.includes(cat)) {
        if (!grouped[cat]) grouped[cat] = [];
        if (!grouped[cat].includes(item)) grouped[cat].push(item);
      }
    }
    return grouped;
  };

  const groupedItems = buildGrouped(generatedItems);
  const groupedManualItems = buildGrouped(manualItems);

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-32" data-testid="grocery-page">
      {/* Header */}
      <div className="pl-[72px] pr-5 pt-6 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-3xl text-[#3D4035]">{t("groceryTitle")}</h1>
            {items.length > 0 && (
              <p className="font-body text-sm text-[#A5A58D] mt-1">{checkedCount}/{items.length} {t("itemsChecked")}</p>
            )}
          </div>
          {/* Clear All Button - removed from header, now at bottom */}
        </div>
      </div>

      {/* Progress bar */}
      {items.length > 0 && (
        <div className="px-5 mb-4">
          <div className="w-full h-2 bg-[#F2E9E4] rounded-full overflow-hidden">
            <div className="h-full bg-[#5C7A5E] rounded-full transition-all duration-500"
              style={{ width: `${(checkedCount / items.length) * 100}%` }} />
          </div>
        </div>
      )}

      <div className="px-5">
        {/* Tutorial Step 6 - go to fridge (page-level: survives empty list after Purchase) */}
        {tutorialActive && tutorialStep === 6 && (
          <TutorialTooltip step={6} targetId="nav-fridge" />
        )}
        {/* Stale/Sync banner - hide during tutorial */}
        {isStale && !tutorialActive && (
          <div data-testid="grocery-stale-banner" className="mb-5 bg-[#E9C46A]/15 rounded-2xl p-5 border border-[#E9C46A]/30">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-[#8B6914] shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-body font-bold text-[#3D4035]" data-testid="stale-banner-title">{t("syncBannerTitle")}</p>
                <p className="font-body text-sm text-[#6B705C] mt-1">{t("syncBannerMsg")}</p>
              </div>
            </div>
            <button data-testid="grocery-refresh-button"
              onClick={() => handleGenerate(list?.days_count || 1)}
              disabled={generating}
              className="w-full bg-[#7B9E7F] text-white rounded-xl py-3.5 font-body font-bold text-base flex items-center justify-center gap-2 hover:bg-[#6B8E6F] transition-all disabled:opacity-50">
              {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" strokeWidth={1.5} />}
              {t("updateList")}
            </button>
          </div>
        )}

        {offCatalogPurged > 0 && (
          <div className="mb-5 bg-[#E9C46A]/15 rounded-2xl p-5 border border-[#E9C46A]/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-[#8B6914] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="font-body text-sm text-[#6B705C] flex-1">
                {plural(lang, offCatalogPurged, {
                  one: t("offCatalogPurgedHint_one"),
                  few: t("offCatalogPurgedHint_few"),
                  many: t("offCatalogPurgedHint"),
                }).replace("{n}", offCatalogPurged)}
              </p>
              <button onClick={() => setOffCatalogPurged(0)} className="w-6 h-6 rounded-full hover:bg-black/5 flex items-center justify-center shrink-0">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={2} />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#7B9E7F] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          /* Clean Empty State */
          <div className="text-center py-16" data-testid="grocery-empty-state">
            <div className="w-20 h-20 rounded-full bg-[#7B9E7F]/10 flex items-center justify-center mx-auto mb-5">
              <ShoppingBag className="w-10 h-10 text-[#7B9E7F]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl text-[#3D4035] mb-2">{t("emptyListTitle")}</h2>
            <p className="font-body text-[#A5A58D] mb-8 max-w-xs mx-auto">{t("emptyListMessage")}</p>
            
            {/* Add Item Button */}
            <button
              data-testid="empty-add-item-btn"
              onClick={() => setShowAddModal(true)}
              className="w-full max-w-xs mx-auto mb-4 bg-[#7B9E7F] text-white rounded-2xl py-4 font-body font-bold text-base flex items-center justify-center gap-2.5 hover:bg-[#6B8E6F] transition-all shadow-lg shadow-[#7B9E7F]/20"
            >
              <Plus className="w-5 h-5" strokeWidth={2} />
              {t("addItemManually")}
            </button>
            
            {/* Divider */}
            <div className="flex items-center gap-3 max-w-xs mx-auto mb-4">
              <div className="flex-1 h-px bg-[#E8E5E0]" />
              <span className="font-body text-xs text-[#A5A58D] uppercase tracking-wider">{t("or")}</span>
              <div className="flex-1 h-px bg-[#E8E5E0]" />
            </div>
            
            {/* Generate from meal plan buttons */}
            <p className="font-body text-xs text-[#6B705C] mb-3 uppercase tracking-widest font-bold">{t("generateFromPlan")}</p>
            <div className="space-y-2.5 max-w-xs mx-auto">
              {[1, 3, 7].map((days) => (
                <button key={days} data-testid={`generate-${days}-day`}
                  onClick={() => handleGenerate(days)}
                  disabled={generating}
                  className={`w-full rounded-xl py-3 font-body font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                    days === 1
                      ? "bg-white text-[#7B9E7F] border-2 border-[#7B9E7F] hover:bg-[#7B9E7F]/5"
                      : "bg-white text-[#6B705C] border border-[#E8E5E0] hover:border-[#7B9E7F]"
                  }`}>
                  {generating && generatingDays === days ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  )}
                  {t(`generate${days}Day`)}
                </button>
              ))}
            </div>
            
            {/* Go to Planner link */}
            <button
              data-testid="empty-go-to-planner"
              onClick={() => navigate("/planner")}
              className="mt-6 font-body text-sm text-[#7B9E7F] hover:text-[#4A6B4E] underline underline-offset-4 decoration-[#7B9E7F]/30"
            >
              {t("goToPlanner")}
            </button>
          </div>
        ) : (
          /* Grocery list items by category */
          <div className="space-y-5" data-testid="grocery-items-list">
            {/* Tutorial Step 5 - check items, then point to Purchase */}
            {tutorialActive && tutorialStep === 5 && items.length > 0 && (
              <TutorialTooltip step={5} targetId={checkedCount === items.length ? "purchase-button" : "grocery-items-list"} />
            )}
            {/* Generated items grouped by category */}
            {(() => {
              let globalIndex = 0;
              const renderItem = (item, category, idx) => {
                const currentIndex = globalIndex++;
                return (
                  <button key={`${category}-${idx}`}
                    data-testid={currentIndex === 0 ? "grocery-item-0" : `grocery-item-${item.name}`}
                    onClick={() => toggleItem(item.name, !item.checked)}
                    className="w-full px-4 py-3.5 flex items-center gap-3 text-left hover:bg-[#FAFAF8] transition-colors">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      item.checked ? "bg-[#5C7A5E] border-[#5C7A5E]" : "border-[#A5A58D]"
                    }`}>
                      {item.checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`font-body font-medium ${item.checked ? "line-through text-[#A5A58D]" : "text-[#3D4035]"}`}>
                        {item.localized_name || item.name}
                      </span>
                    </div>
                    {item.amount && (
                      <span className="font-body text-sm text-[#A5A58D] shrink-0">{formatAmount(item.amount, lang)} {formatUnitPlural(item.unit, item.amount, lang)}</span>
                    )}
                  </button>
                );
              };

              return (
                <>
                  {Object.entries(groupedItems).map(([category, catItems]) => (
                    <div key={category} data-testid={`grocery-category-${category}`}>
                      <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5">
                        <span>{CATEGORY_ICONS[category] || "📦"}</span>
                        {t(`cat_${category}`) || category}
                      </h3>
                      <div className="bg-white rounded-2xl border border-black/5 divide-y divide-[#F2E9E4]">
                        {catItems.map((item, idx) => renderItem(item, category, idx))}
                      </div>
                    </div>
                  ))}

                  {/* Manual items section */}
                  {manualItems.length > 0 && (
                    <div data-testid="grocery-manual-section">
                      <div className="flex items-center gap-2 mb-2 mt-1">
                        <div className="flex-1 h-px bg-[#E8E5E0]" />
                        <span className="font-body text-xs text-[#A5A58D] uppercase tracking-widest font-bold px-1">
                          {t("manualItemsSection")}
                        </span>
                        <div className="flex-1 h-px bg-[#E8E5E0]" />
                      </div>
                      {Object.entries(groupedManualItems).map(([category, catItems]) => (
                        <div key={`manual-${category}`} className="mb-4" data-testid={`grocery-manual-category-${category}`}>
                          <h3 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5">
                            <span>{CATEGORY_ICONS[category] || "📦"}</span>
                            {t(`cat_${category}`) || category}
                          </h3>
                          <div className="bg-white rounded-2xl border border-black/5 divide-y divide-[#F2E9E4]">
                            {catItems.map((item, idx) => renderItem(item, `manual-${category}`, idx))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Batch re-generate buttons */}
            <div className="pt-2">
              <p className="font-body text-xs text-[#A5A58D] uppercase tracking-widest font-bold mb-2">{t("regenerateFor")}</p>
              <div className="flex gap-2">
                {[1, 3, 7].map((days) => (
                  <button key={days} data-testid={`regen-${days}-day`}
                    onClick={() => handleGenerate(days)} disabled={generating}
                    className="flex-1 bg-white border border-[#E8E5E0] text-[#6B705C] rounded-xl py-2.5 font-body text-sm font-semibold hover:border-[#7B9E7F] hover:text-[#7B9E7F] transition-all disabled:opacity-50 flex items-center justify-center gap-1.5">
                    {generating && generatingDays === days ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {t(`gen${days}Short`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Purchase + Smart Check */}
            <div className="pt-4 pb-8 space-y-3">
              {checkedCount > 0 && (
                <button data-testid="purchase-button" onClick={handlePurchase} disabled={purchasing}
                  className="w-full bg-[#5C7A5E] text-white rounded-2xl py-4 font-body font-bold text-base flex items-center justify-center gap-2.5 hover:bg-[#4A6B4E] transition-all shadow-lg shadow-[#5C7A5E]/20 disabled:opacity-50 active:scale-[0.98]">
                  {purchasing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />}
                  {t("purchased")} ({checkedCount})
                </button>
              )}
              <button data-testid="smart-check-button"
                onClick={() => { /* placeholder */ }}
                className="w-full bg-white border-2 border-dashed border-[#E8E5E0] text-[#A5A58D] rounded-2xl py-4 font-body font-semibold flex items-center justify-center gap-2.5 hover:border-[#7B9E7F] hover:text-[#7B9E7F] transition-all">
                <Camera className="w-5 h-5" strokeWidth={1.5} />
                {t("smartCheck")}
                <span className="text-xs bg-[#E9C46A]/20 text-[#8B6914] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                  <Crown className="w-3 h-3" strokeWidth={1.5} /> Premium
                </span>
              </button>

              {/* Clear All Button */}
              {items.length > 0 && (
                <button
                  data-testid="clear-all-btn"
                  onClick={() => setShowClearConfirm(true)}
                  className="w-full bg-white border border-[#E8E5E0] text-[#BC4749] rounded-2xl py-3.5 font-body font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#BC4749]/5 hover:border-[#BC4749]/30 transition-all"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  {t("clearAll")}
                </button>
              )}

          
            </div>
          </div>
        )}
      </div>

      {/* Floating + button */}
      {list?.id && items.length > 0 && !showAddModal && (
        <button data-testid="add-grocery-item-btn"
          onClick={openAddModal}
          className="fixed bottom-24 right-5 w-14 h-14 rounded-full bg-[#7B9E7F] text-white shadow-lg shadow-[#7B9E7F]/30 flex items-center justify-center hover:bg-[#6B8E6F] transition-all active:scale-95 z-[100]">
          <Plus className="w-6 h-6" strokeWidth={2} />
        </button>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-[10001] flex items-start sm:items-center justify-center p-3 pt-6 overflow-y-auto" data-testid="add-item-modal">
          <div className="bg-white w-full max-w-md rounded-2xl max-h-[85dvh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="font-display text-lg font-bold text-[#3D4035]">{t("addItemTitle")}</h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <X className="w-5 h-5 text-[#6B705C]" />
              </button>
            </div>

            {/* Search input */}
            <div className="p-4 border-b border-gray-100">
              <input
                type="text"
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                placeholder={t("searchOrType")}
                data-testid="add-item-search"
                autoFocus
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#7B9E7F] focus:outline-none font-body text-base"
              />

              {/* Suggestions dropdown */}
              {addSearch.trim().length >= 1 && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-100 rounded-xl">
                  {suggestions.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => addCatalogChip(s)}
                      data-testid={`suggestion-${idx}`}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#7B9E7F]/10 text-left border-b border-gray-50 last:border-0"
                    >
                      {s.icon && <span className="text-base">{s.icon}</span>}
                      <span className="font-body text-sm text-[#3D4035]">{s.name}</span>
                    </button>
                  ))}
                  {/* Off-catalog: add typed text as new ingredient */}
                  {!suggestions.some(s => s.name.toLowerCase() === addSearch.trim().toLowerCase()) && (
                    <button
                      onClick={addCustomChip}
                      data-testid="add-custom-item"
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#E9C46A]/15 text-left"
                    >
                      <span className="text-base">➕</span>
                      <span className="font-body text-sm text-[#3D4035]">
                        {addSearch.trim()} <span className="text-xs text-[#6B705C]">— {t("newIngredientThanks")}</span>
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Staged chips */}
            <div className="flex-1 overflow-y-auto p-4">
              {stagedItems.length === 0 ? (
                <p className="text-center text-sm text-[#6B705C] font-body py-8">{t("stagingEmpty")}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {stagedItems.map((s, idx) => {
                    const accent = s.off_catalog ? "#E9C46A" : "#7B9E7F";
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-2 rounded-xl border w-full min-w-0"
                        style={{ backgroundColor: `${accent}1A`, borderColor: `${accent}33` }}
                      >
                        {s.off_catalog ? <span className="text-base">🆕</span> : (s.icon && <span className="text-base">{s.icon}</span>)}
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm font-semibold text-[#3D4035] truncate">{s.name}</p>
                          {s.off_catalog && <p className="text-xs text-[#6B705C]">{t("newIngredientThanks")}</p>}
                        </div>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={s.amount}
                          onChange={(e) => updateChip(idx, "amount", e.target.value)}
                          placeholder={t("amountPlaceholder")}
                          className="w-14 shrink-0 px-2 py-1 rounded-lg border border-gray-200 focus:border-[#7B9E7F] focus:outline-none font-body text-base text-center"
                        />
                        <select
                          value={s.unit}
                          onChange={(e) => updateChip(idx, "unit", e.target.value)}
                          className="w-16 shrink-0 px-1 py-1 rounded-lg border border-gray-200 focus:border-[#7B9E7F] focus:outline-none font-body text-base bg-white"
                        >
                          {["шт", "кг", "г", "л", "мл"].map(u => <option key={u} value={u}>{formatUnit(u, lang)}</option>)}
                        </select>
                        <button
                          onClick={() => removeChip(idx)}
                          className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors flex-shrink-0"
                        >
                          <X className="w-3.5 h-3.5 text-[#6B705C]" strokeWidth={2} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleAddToCart}
                disabled={stagedItems.length === 0 || addingItem}
                className="w-full py-3 rounded-xl bg-[#7B9E7F] text-white font-display font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#6a8a6e] transition-colors"
              >
                {addingItem ? "..." : t("addToCart")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-center justify-center px-5" data-testid="clear-confirm-modal">
          <div className="w-full max-w-sm bg-[#FAFAF8] rounded-3xl p-6 animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-[#BC4749]/10 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-[#BC4749]" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl text-[#3D4035] text-center mb-2">
              {t("clearListTitle")}
            </h3>
            <p className="font-body text-sm text-[#A5A58D] text-center mb-6">
              {t("clearListMessage")}
            </p>
            <div className="flex gap-3">
              <button
                data-testid="clear-cancel-btn"
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-3.5 rounded-xl bg-white border border-[#E8E5E0] font-body font-semibold text-[#6B705C] hover:bg-[#F2E9E4] transition-all"
              >
                {t("cancel")}
              </button>
              <button
                data-testid="clear-confirm-btn"
                onClick={handleClearAll}
                disabled={clearing}
                className="flex-1 py-3.5 rounded-xl bg-[#BC4749] font-body font-semibold text-white hover:bg-[#A63D3F] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={2} />}
                {t("clearAll")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
